import * as React from "react";
import {
  AriaListBoxOptions,
  AriaListBoxSectionProps,
  AriaOptionProps,
  useListBox,
  useListBoxSection,
  useOption,
} from "react-aria";
import { ListState, Node, useListState } from "react-stately";
import { CollectionChildren, ID, StyleProps } from "../../utils/types";
import { Text } from "../text";
import { Flex } from "../flex";
import { useListBoxContent } from "./list-box.utils";
import { Color } from "../../utils/theme";
import { ListBoxProvider, useListBoxContext } from "./list-box-provider";
import { useTheme } from "../theme";
import { cx, mergeRefs } from "../../utils/dom";
import {
  CollectionItemProps,
  CollectionSectionProps,
} from "../collections/collections.types";
export interface ListBoxRenderProps<T> {
  children?: CollectionChildren<T>; // TODO - type this properly
  labelKey?: keyof T | string; // TODO - type this properly
  valueKey?: keyof T | string; // TODO - type this properly
  sectionKey?: keyof T;
  state?: ListState<T>;
  color?: Color;
  width?: number | string;
  showSectionSeparator?: boolean;
  showSelectionIndicator?: boolean;
  renderOption?: (item: T) => React.ReactNode;
  renderSelectionIndicator?: (item: T) => React.ReactNode;
  renderSectionLabel?: (item: T) => React.ReactNode;
  renderEmpty?: () => React.ReactNode;
}

export interface ListBoxProps<T>
  extends AriaListBoxOptions<T>,
    ListBoxRenderProps<T>,
    StyleProps<"wrapper" | "label" | "section" | "option"> {
  listBoxRef?: React.RefObject<HTMLUListElement>;
}

export function ListBox<T extends object>({
  state,
  color = "gray",
  width = 300,
  listBoxRef,
  renderEmpty,
  classNames,
  className,
  style,
  styles,
  ...props
}: ListBoxProps<T>) {
  const children = useListBoxContent(props);
  const internalState = useListState({
    ...props,
    children: children,
  });
  const listRef = React.useRef(null);
  const listState = state || internalState;
  const { listBoxProps } = useListBox(
    { ...props, label: props.label ?? "Select an option" },
    listState,
    listRef
  );

  const options = [...listState.collection];

  const theme = useTheme();

  function renderEmptyState() {
    return (
      renderEmpty?.() ?? (
        <Text color="gray" variant="lighter">
          No options
        </Text>
      )
    );
  }

  return (
    <ListBoxProvider {...props}>
      <Flex
        as="ul"
        stack
        gap="none"
        className={cx("p-list-box-wrapper", classNames?.wrapper, className)}
        ref={mergeRefs(listRef, listBoxRef)}
        data-radius="lg"
        data-color={color ?? theme?.color}
        style={{ width: width, ...style, ...styles?.wrapper }}
        {...listBoxProps}
      >
        {options.length === 0 && (
          <Flex justify="center" align="center" style={{ height: 50 }}>
            {renderEmptyState()}
          </Flex>
        )}
        {options.map((item) => {
          return item.type === "section" ? (
            <ListSection
              className={classNames?.section}
              section={item}
              state={listState}
              key={item.key}
            />
          ) : (
            <ListOption
              className={classNames?.option}
              state={listState}
              item={item}
              key={item.key}
            />
          );
        })}
      </Flex>
    </ListBoxProvider>
  );
}

interface OptionProps<T> extends AriaOptionProps, StyleProps {
  item: Node<T>;
  state: ListState<T>;
}

function ListOption({ state, item }: OptionProps<any>) {
  const optionRef = React.useRef(null);
  const {
    optionProps,
    isDisabled,
    isFocusVisible,
    isFocused,
    isPressed,
    isSelected,
  } = useOption({ key: item.key }, state, optionRef);

  const { showSelectionIndicator = true, renderSelectionIndicator } =
    useListBoxContext().props;

  const {
    className,
    classNames,
    style,
    styles,
    color,
    textValue,
    title,
    ...otherProps
  } = item.props as CollectionItemProps<any>;

  return (
    <li
      style={{ ...style, ...styles?.wrapper }}
      className={cx(className, "p-list-option", classNames?.wrapper)}
      data-disabled={isDisabled}
      data-focus-visible={isFocusVisible}
      data-focused={isFocused}
      data-pressed={isPressed}
      data-selected={isSelected}
      data-color={color}
      {...optionProps}
      {...otherProps}
      ref={optionRef}
      data-text-value={textValue}
      data-title={title}
    >
      {item.rendered}
      {isSelected &&
        showSelectionIndicator &&
        (renderSelectionIndicator?.(item) ?? <div>/</div>)}
    </li>
  );
}

type ListSectionProps<T> = AriaListBoxSectionProps &
  StyleProps<"wrapper" | "separator" | "heading" | "items"> & {
    __parrotKey?: ID;
    state: ListState<T>;
    section: Node<T>;
  };
function ListSection({ state, section }: ListSectionProps<any>) {
  const { itemProps, headingProps, groupProps } = useListBoxSection({
    heading: section?.rendered,
    "aria-label": section["aria-label"],
  });

  const listBoxContext = useListBoxContext();

  const { className, classNames, style, styles } =
    section.props as CollectionSectionProps<any>;

  const children = React.useMemo(() => {
    if (state.collection.getChildren) {
      return [...state.collection.getChildren(section.key)];
    }

    return [];
  }, []);

  return (
    <Flex
      gap="sm"
      stack
      className={cx(className, "p-list-section", classNames?.wrapper)}
      style={{ ...style, ...styles?.wrapper }}
    >
      {section.key !== state.collection.getFirstKey() &&
        listBoxContext.props.showSectionSeparator && (
          <li
            style={styles?.separator}
            className={cx("p-list-separator", classNames?.separator)}
          ></li>
        )}

      <Flex gap={"none"} stack as="li" {...itemProps}>
        {section.rendered && (
          <Text
            style={styles?.heading}
            className={cx("p-list-section-header", classNames?.heading)}
            as="div"
            variant="light"
            {...headingProps}
          >
            {section.rendered}
          </Text>
        )}
        <ul className={cx(classNames?.items)} {...groupProps}>
          {children.map((node) => {
            return <ListOption item={node} key={node.key} state={state} />;
          })}
        </ul>
      </Flex>
    </Flex>
  );
}
